<template>
    <div class="bg-growth rounded-full text-white p-2 fixed top-5 px-6 left-1/2 z-40" v-if="saved">{{ $t('saved') }}</div>
    <div v-if="modelData" class="w-full mb-7 -mt-3 relative">
        <slot v-if="!noarrow">
            <router-link to="../models" v-if="$route.query.e">
                <font-awesome-icon icon="fas fa-arrow-left" class="float-left text-lg p-2 text-button -mt-2" />
            </router-link>
            <router-link to="../../settings" v-else>
                <font-awesome-icon icon="fas fa-arrow-left" class="float-left text-lg p-2 text-button -mt-2" />
            </router-link>
        </slot>
    </div>

    <div v-if="loading" class="text-center">
        <font-awesome-icon icon="fas fa-spinner" class="animate-spin" />
    </div>
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-10 max-w-screen-lg">
        <div class="hidden sm:block mt-2">
            <div v-if="$route.query.e || model_id_prop" class="flex items-center w-max bg-sepBorder bg-opacity-40 dark:bg-dark.tableBG dark:border-dark.sepBorder p-3 px-5 rounded">
                <div class="rounded-full h-24 w-24 bg-gray-200 dark:bg-dark.panels" :style="{ backgroundImage: `url(${modelData.avatar})`, backgroundSize: 'cover', backgroundPosition: 'center'}"></div>
                <div class="ml-8">
                    <h2 class="font-bold">{{modelData.username ? '@' + modelData.username : modelData.free.name}}</h2>
                    <div class="mt-1" v-if="modelData.platformName">
                        <img :src="$platformLogo(modelData)" :title="modelData.platformName" :alt="modelData.platformName" class="h-5"/>
                    </div>
                    <span class="font-light text-sm">{{modelData.free.id ? $t('id') + ': ' + modelData.free.id : null}}</span>
                    <div v-if="[1,2].includes(modelData.platform)" @click="modal.type = 'scraping', modal.display = true" :title="$t('scraping_notice')" class="cursor-pointer rounded border border-button dark:text-growth dark:border-growth p-1.5 px-3 text-button font-bold mt-2 text-sm">{{ $t('scrape_button') }}</div>
                </div>
            </div>
            <slot v-else>
                <h3 class="text-xl font-bold leading-relaxed">{{ $t('creator_modal.h1') }}</h3>
                <p>{{ $t('creator_modal.p1') }}</p>
            </slot>

            <h3 class="text-xl font-bold leading-relaxed mt-5">{{ $t('creator_modal.h2') }}</h3>
            <p v-html="$t('creator_modal.p2')"></p>
        </div>
        <div v-if="model_id_prop == null || modelData?.pair_id != null" class="text-panelText pb-1">
            <h2 class="text-2xl text-button dark:text-growth font-bold mb-2" v-if="$route.query.e || model_id_prop">{{ $t('edit_creator') }}</h2>
            <h2 class="text-2xl text-button dark:text-growth font-bold mb-2" v-else>{{ $t('add_creator') }}</h2>
            <span v-if="!($route.query.e || model_id_prop)" class="cursor-pointer text-sm text-button dark:text-dark.textOnBkg">
                <MazCheckbox v-model="advancedSettings" label="Advanced settings" />
            </span>
            <div class="grid mt-4" :class="{'grid-cols-2 gap-3' : !$route.query.e && model_id_prop == null}">
                <MazSelect v-if="!$route.query.e && model_id_prop == null"
                    v-model="modelData.platform" 
                    :options="platforms.map(platform => ({ value: platform.id, label: platform.name }))" 
                    :label="$t('platform')" 
                    :placeholder="$t('platform')" 
                    :clearable="true"
                />
                <MazInput 
                    v-model="modelData.free.name"
                    :label="$t('stage_name')"
                    :hint="submitted && !modelData.free.name ? 'Required' : null"
                    :error="submitted && !modelData.free.name"
                />
            </div>
            <div class="rounded-lg">
                <div class="leading-none">
                    <div class="mt-3 text-xs text-gray-500 dark:text-growth">{{ $t('login_details') }}</div>
                    <div class="grid grid-cols-2 gap-3 my-1">
                        <MazInput
                            v-model="modelData.free.email"
                            :label="$t('reg_form.email')"
                            :hint="submitted && !modelData.free.email ? 'Required' : null"
                            :error="submitted && !modelData.free.email"
                        />
                        <MazInput
                            v-model="modelData.free.password"
                            :label="$t('reg_form.password')"
                            :hint="submitted && !modelData.free.password ? 'Required' : null"
                            :error="submitted && !modelData.free.password"
                            type="password"
                        />
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-3 mt-4" v-if="groups.length>0">
                <MazSelect v-if="groups && groups.length>0"
                    v-model="modelData.group_id"
                    :options="[{ id: null, name: $t('all_groups') }, ...groups]"
                    :label="$t('group')"
                    optionValueKey="id"
                    optionLabelKey="name"
                    option-input-value-key="name"
                />

                <MazSelect v-if="selectedTeams && selectedTeams.length>0"
                    v-model="modelData.team_id"
                    :options="[{ id: null, name: $t('all_teams') }, ...selectedTeams]"
                    :label="$t('team')"
                    optionValueKey="id"
                    optionLabelKey="name"
                    option-input-value-key="name"
                />
            </div>
            <div v-else class="bg-opacity-50 dark:bg-dark.tableBG dark:bg-opacity-100 p-2 pt-0.5 rounded mt-3" :class="submitted && !modelData.groupName ? 'bg-red-100' : 'bg-sepBorder'">
                <div class="grid grid-cols-2 gap-1 sm:gap-5 py-1">
                    <MazInput 
                        :label="$t('group_name')"
                        v-model="modelData.groupName"
                    />
                    <button @click="modal.type = 'group', modal.display = true" class="border-2 border-button rounded-lg text-button dark:text-growth dark:border-growth font-bold w-full text-center text-sm sm:text-base">{{ $t('group_settings.headline') }} <font-awesome-icon icon="fa-solid fa-arrow-up-right-from-square" class="ml-1" /></button>
                </div>
                <p class="text-xs">{{ $t('group_warning') }}</p>
            </div>

            <div class="clear-both"></div>

            <section class="mt-3">
                <slot v-if="advancedSettings || ($route.query.e || model_id_prop)">
                    <MazSelect 
                        :label="$t('pick_country')"
                        v-model="modelData.countryCode"
                        :options="countryList"
                        optionValueKey="id"
                        optionLabelKey="name"
                        option-input-value-key="name"
                    />

                    <div class="mt-3">
                        <div v-if="!customProxy">
                            <MazSelect 
                                v-if="modelData.proxy == null || modelData.proxy > 0"
                                :label="$t('proxy') + ' (' + $t('if_needed') + ') +$1'"
                                v-model="modelData.proxy"
                                :options="[{ id: null, value: $t('no_proxy') }, ...proxy_list]"
                                optionValueKey="id"
                                optionLabelKey="value"
                                option-input-value-key="value"
                            />
                            <div v-else class="flex rounded-2xl border border-gray-400 px-2 items-center justify-between">
                                <span class="truncate w-96 text-sm">{{modelData.proxy}}</span>
                                <button @click="removeProxy()" class="ml-2 p-1 text-red-400 cursor-pointer right-0"><font-awesome-icon icon="fas fa-times" /></button>
                            </div>
                            <div class="text-xs text-gray-500 mt-0.5">{{ $t('proxy_notice') }}<span class="font-bold cursor-pointer underline dark:text-gray-400 text-black hover:bg-growth dark:hover:text-black p-1 rounded" @click="useCustomProxy(true)">{{ $t('proxy_notice_2') }}</span></div>
                        </div>
                        <div v-else>
                            <MazInput 
                                v-model="modelData.proxy"
                                :label="$t('custom_proxy')"
                            />
                            <div class="text-xs text-gray-500 mt-0.5">{{ $t('custom_proxy_notice') }} - <span class="font-bold cursor-pointer underline text-black dark:text-gray-400 hover:bg-growth dark:hover:text-black p-1 rounded" @click="customProxy = false">{{ $t('common_proxy') }}</span></div>
                        </div>
                    </div>

                    <div class="clear-both mt-3 flex items-center">
                        <div class="mr-2" v-tooltip="$t('commission_tooltip')">
                            <font-awesome-icon icon="far fa-question-circle" class="text-gray-500 dark:text-growth" />
                        </div>
                        <MazSelect 
                            class="w-32 mr-2"
                            :label="$t('commission')"
                            v-model="modelData.commission_type"
                            :options="[{value:'%',label:'%'}, {value:'$',label:'$'}]"
                        />
                        <MazInput 
                            v-model="modelData.commission"
                            :label="$t('commission')"
                            type="number"
                        />
                    </div>
                </slot>

                <div v-if="modelData.free.tools && modelData.free.tools.length>0" @click="modal.type = 'tools', modal.display = true" class="border-b border-sepBorder dark:border-dark.sepBorder relative rounded mt-2 w-full cursor-pointer pl-1">
                    <label class="text-xs text-gray-500 dark:text-dark.panelsText">{{ $t('Tools') }}</label>
                    <span class="text-sm w-full sm:w-72 block whitespace-nowrap overflow-ellipsis overflow-hidden py-1">
                        <slot v-for="(tool,index) in modelData.free.tools" :key="index">
                            <slot v-if="tool.active==1">{{tool.name}}<slot v-if="index<modelData.free.tools.length-1">, </slot></slot>
                        </slot>
                    </span>
                    <font-awesome-icon icon="fa-xs fas fa-pen-to-square" class="absolute cursor-pointer right-3 top-5 text-panelsText dark:text-dark.panelsText" />
                </div>

                <div v-if="1==2" @click="openModelAccessModal()" class="bg-opacity-10 bg-white rounded p-2 px-3 w-max ml-10 mt-5 relative cursor-pointer">
                    <h1>Model dashboard access <font-awesome-icon icon="fas fa-arrow-up-right-from-square" class="ml-1.5"></font-awesome-icon></h1>
                </div>
                <button v-if="$route.query.routedFrom != 'archived'" @click="saveModel()" class="delay-75 duration-75 transition-all mt-5 bg-button rounded cursor-pointer p-2 px-5 text-buttonText dark:text-black dark:bg-growth text-center font-semibold">
                    <font-awesome-icon icon="fas fa-spinner" class="animate-spin" v-if="buttonLoading" /> {{buttonValue}}
                </button>
            </section>
        </div>
        <div v-else class="text-center">
            <font-awesome-icon icon="fas fa-spinner" class="animate-spin" />
        </div>
        <div class="my-5 col-span-2 text-button dark:text-growth clear-both text-center text-sm sm:text-base">{{ $t('loginmate_notice_1') }} <a :href="$router.resolve({ name: 'Tools' }).href" target="_blank" class="font-bold underline">{{ $t('desktop_app') }}. </a>{{ $t('loginmate_notice_2') }}<br /></div>
    </div>
    <div class="clear-both"></div>

    <transition name="zoom-fade">
        <section v-if="modal.display" @click="modal.display = false" class="backdrop-blur w-full h-full fixed inset-0 bg-gray-700 bg-opacity-30 z-50 flex justify-center items-center px-4 text-black">
            <section @click.stop class="bg-white dark:bg-dark.bkg dark:text-dark.textOnBkg rounded-lg w-full max-h-screen max-w-md sm:max-w-5xl mx-auto my-auto p-8 sm:p-12 z-50 overflow-auto relative">
                <font-awesome-icon @click="modal.display = false" icon="fas fa-xmark" class="transition absolute p-6 top-0 right-0 text-xl cursor-pointer hover:opacity-50" />
                <groups-panel v-if="modal.type == 'group'" :noarrow="true" />
                <scraper-panel v-if="modal.type == 'scraping'" :model="modelData" />
                <tools-panel v-if="modal.type == 'tools'" :model="modelData" />
            </section>
        </section>
    </transition>
</template>

<script setup>
    import modelAccess from "@/components/settings/models/model_access.vue"
    import groupsPanel from "@/components/settings/groups/groupsSettings.vue"
    import scraperPanel from "@/components/settings/models/scraping.vue"
    import toolsPanel from "@/components/settings/models/tools.vue"
</script>

<script>
    import { useAuthStore } from '@/stores/auth';
    import { get_platforms, get_groups, archive_model, get_models,get_list_data,save_model, get_proxy_list, remove_proxy_from_model } from '@/api/api';
    export default {
        props: ['noarrow', 'model_id_prop'],
        emits: ['close'],
        components: {modelAccess, groupsPanel, toolsPanel, scraperPanel},
        computed: {
            authStore() {
                return useAuthStore();
            },
            myself() {
                return this.authStore.myself;
            },
            access() {
                return this.authStore.access;
            },
            passStyle() {
                return !this.displayPass.f ? { '-webkit-text-security': 'disc' } : {}
            },
            selectedTeams() {
                if (this.modelData.group_id) {
                    const selectedGroup = this.groups.find(group => group.id == this.modelData.group_id);
                    return selectedGroup ? selectedGroup.children : [];
                }
                return [];
            }
        },
        data() {
            return {
                platforms: [],
                customProxy: false,
                advancedSettings: false,
                proxy_list: [{id:1, proxy_address: '0.0.0.0.', port: '0000', country: 'US'}],
                modal: {type:null, display: false },
                dialog: {type:1,show:false,title:'Change the tools settings',data:{tool:null,model:null}},
                groups: [],
                showVIP: false,
                modelData: {
                    pair_id: null,
                    groupName:null,
                    commission_type: "%",
                    free: {name: null, email:null, password:null, accountId: {filled:false, value:null},tools:[]},
                    paid: {active: true, name: null, email:null, password:null, accountId: {filled:false, value:null},tools:[]},
                    code: null,
                    platform: '1',
                    commission: null,
                    countryCode:0,
                    group_id:1,
                    team_id:null,
                    platformName: null,
                    proxy:null
                },
                loading: false,
                buttonLoading:false,
                buttonValue: this.$t('create_account'),
                saved:false,
                submitted:false,
                groupIndex:null,
                displayForm:false,
                displayPass: {f:false,p:false,m:false},
                countryList:null,
                modelPairID:this.$route.query.id,
                codePartOne: "001",
                modelAccessModal:false,
                modelCred: {email:null,password:null}
            }
        },
        methods: {
            async removeProxy() {
                if (confirm(this.$t('you_sure') + this.$t('remove_proxy_from_model'))) {
                    //send an API call to remove the proxy from the model
                    await remove_proxy_from_model(this.$cookies.get('userToken'),this.$cookies.get('c_id'),this.modelData.pair_id,this.modelData.proxy);

                    //when finished
                    this.modelData.proxy = null;
                }
            },
            seePass(condition) {
                return !condition ? 'fas fa-eye' : 'fas fa-eye-slash';
            },
            updateSelectedGroup() {
                this.modelData.team_id = 0;  // reset the selected team whenever the group changes
            },
            pad(num, size) {
                while (num.length < size) num = "0" + num;
                return num;
            },
            useCustomProxy(bl) {
                this.customProxy = bl;
                if (bl && this.modelData.proxy_type == 0) {
                    this.modelData.proxy = null;
                }
            },
            validateProxyFormat() {
                const regex = /^(?:(\d{1,3}\.){3}\d{1,3}|[a-zA-Z0-9.-]+):\d{1,5}:[a-zA-Z0-9-]+:[a-zA-Z0-9-]+$/;
                const isValid = regex.test(this.modelData.proxy);
                return isValid;
            },
            async saveModel() {
                if (this.modelData.groupName || this.groups.length == 0) {
                    this.modelData.group_id = null;
                }

                if (this.customProxy && this.modelData.proxy && !this.validateProxyFormat()) {
                    alert('Proxy format is incorrect');
                    return false;
                }

                this.submitted = true;

                if (this.modelData.free.name && this.modelData.free.email && this.modelData.free.password && (this.modelData.groupName || this.modelData.group_id)) {
                    this.buttonLoading = true
                    if (this.modelPairID) {
                        this.buttonValue = `${this.$t('updating')}...`;
                        let save_b_label = this.$t('save_changes');
                        setTimeout(function() {
                            this.buttonLoading = false;
                            this.buttonValue = save_b_label;
                        }.bind(this), 1000);
                    }else {
                        this.buttonValue = `${this.$t('creating')}...`;
                        let create_b_label = this.$t('create_account');
                        setTimeout(function() {
                            this.buttonLoading = false;
                            this.buttonValue = create_b_label;
                        }.bind(this), 1000);
                    }
                } else {
                    alert(this.$t('fill_required_fields'));
                    return;
                }
                
                const result = await save_model(this.$cookies.get('userToken'),this.$cookies.get('c_id'),this.modelData);
                if (result && result.message) {
                    //change to just a save div popup instead of an alert
                    if (!result.success) {alert(result.message);}
                    if (result.pair_id) {
                        this.$emit('close', result.pair_id);
                    }
                    this.$emit('close', null);
                    return;
                } else {
                    if(!this.modelPairID) {this.modelData = {platform:this.modelData.platform, free: {name: null,accountId: {filled:false,value:null}},paid: {name: null,accountId: {filled:false,value:null}},code: null}; this.submitted = false;}
                    this.saved = true;
                    if (!this.noarrow) {
                        setTimeout(() => (this.saved = false, this.$router.push({name:'models'}), 2000));
                    } else {
                        this.$router.push({name: 'Creators'});
                        setTimeout(() => {this.saved = false;}, 500);
                        setTimeout(() => this.$emit('close'), 500)
                    }
                }
                this.submitted = false;
            },
            async paidToArchiveAndBack(active) {
                await archive_model(this.$cookies.get('userToken'),this.$cookies.get('c_id'),active,null,this.modelData.paid.id);
                this.modelData.paid.active = active;
            },
            async getTeamsAndGroups() {
                this.groups = await get_groups(this.$cookies.get("userToken"),this.$cookies.get('c_id'));
                if (this.groups && this.groups.length) {this.displayForm = true;}
                if (this.groups.length == 1) {this.modelData.group_id = this.groups[0].id;}
            }
        },
        async created() {
            //get list of countries
            this.loading = true;
            this.countryList = await get_list_data(this.$cookies.get('userToken'),this.$cookies.get('c_id'),'getCountryCodes',null,null,null,null,null);
            if (this.model_id_prop > 0) {this.modelPairID = this.model_id_prop;}
            if (this.modelPairID) {
                this.buttonValue = this.$t('save_changes');
                this.modelData = await get_models(this.$cookies.get('userToken'),this.$cookies.get('c_id'),null,this.modelPairID);
                if (this.modelData && this.modelData.paid && this.modelData.paid.email) {
                    this.showVIP = true;
                }
                if (this.modelData.proxy) {
                    this.customProxy = Boolean(this.modelData.proxy_type);
                }
                this.getTeamsAndGroups();
            } else {
                // add a new model
                this.getTeamsAndGroups();
            }
            this.proxy_list = await get_proxy_list(this.$cookies.get('userToken'),this.$cookies.get('c_id'));
            if (Array.isArray(this.proxy_list)) {
                this.proxy_list = this.proxy_list.map(proxy => {
                    return {id: proxy.id, value: proxy.country + ' - ' + proxy.proxy_address};
                });
            }
            this.loading = false;

            this.platforms = await get_platforms(this.$cookies.get('userToken'),this.$cookies.get('c_id'));
        }
    }
</script>